// Function index:
//   mountNpcPanel({ worldId, ... }) — wire up the "NPCs at this hex" panel.
//       Returns { refresh(stateForKey) } so the inspector can drive it when
//       the selected hex changes.

import { createNpc, deleteNpc, listNpcs, type NpcRow } from '../../api.js';
import { escHtml } from './types.js';

export interface NpcPanelHandle {
  refresh(selected: { q: number; r: number } | null): void;
}

export async function mountNpcPanel(
  worldId: number,
  getSelected: () => { q: number; r: number } | null,
): Promise<NpcPanelHandle> {
  const $ = (id: string): HTMLElement => document.getElementById(id)!;
  const npcSection = $('hex-npcs');
  const npcListEl = $('npc-list') as HTMLDivElement;
  const addNpcBtn = $('add-npc-btn') as HTMLButtonElement;
  const addNpcForm = $('add-npc-form') as HTMLFormElement;
  const addNpcCancel = $('add-npc-cancel') as HTMLButtonElement;
  let npcCache: NpcRow[] = (await listNpcs(worldId)).npcs;

  const draw = (): void => {
    const sel = getSelected();
    if (!sel) { npcSection.hidden = true; return; }
    npcSection.hidden = false;
    const here = npcCache.filter((n) => n.q === sel.q && n.r === sel.r);
    npcListEl.innerHTML = '';
    if (here.length === 0) {
      const meta = document.createElement('div');
      meta.className = 'party-meta';
      meta.textContent = 'No NPCs here yet.';
      npcListEl.appendChild(meta);
      return;
    }
    for (const n of here) {
      const row = document.createElement('div');
      row.className = 'party-row';
      row.style.cursor = 'default';
      row.innerHTML = `
        <div>
          <div>${escHtml(n.name)}</div>
          <div class="party-meta">${escHtml(n.archetype)}</div>
        </div>
        <button class="secondary" data-id="${n.id}">×</button>
      `;
      row.querySelector('button')!.addEventListener('click', async () => {
        if (!confirm(`Delete ${n.name}?`)) return;
        await deleteNpc(n.id);
        npcCache = npcCache.filter((x) => x.id !== n.id);
        draw();
      });
      npcListEl.appendChild(row);
    }
  };

  addNpcBtn.addEventListener('click', () => { addNpcForm.hidden = false; addNpcBtn.hidden = true; });
  addNpcCancel.addEventListener('click', () => { addNpcForm.hidden = true; addNpcBtn.hidden = false; addNpcForm.reset(); });
  addNpcForm.addEventListener('submit', async (e) => {
    e.preventDefault();
    const sel = getSelected();
    if (!sel) return;
    const fd = new FormData(addNpcForm);
    let base_state: unknown = {};
    try { base_state = JSON.parse(String(fd.get('base_state') ?? '{}') || '{}'); } catch { /* keep default */ }
    const result = await createNpc(worldId, {
      q: sel.q, r: sel.r,
      name: String(fd.get('name')),
      description: String(fd.get('description') ?? ''),
      archetype: String(fd.get('archetype') ?? 'person'),
      base_state,
    });
    npcCache.push({
      id: result.npc_id, q: sel.q, r: sel.r,
      name: String(fd.get('name')),
      description: String(fd.get('description') ?? ''),
      archetype: String(fd.get('archetype') ?? 'person'),
      base_state: JSON.stringify(base_state),
    });
    addNpcForm.reset();
    addNpcForm.hidden = true;
    addNpcBtn.hidden = false;
    draw();
  });

  return { refresh: draw };
}
